import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUser, FaEnvelope, FaLock, FaGraduationCap, FaBriefcase, 
  FaBuilding, FaCalendarAlt, FaPhone, FaLinkedin, FaChalkboardTeacher,
  FaIndustry, FaMapMarkerAlt, FaGlobe, FaBookOpen
} from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import toast, { Toaster } from 'react-hot-toast';

const InputField = ({ icon: Icon, type, placeholder, value, onChange, name }) => (
  <div className="mb-4 w-full sm:w-1/2 px-2">
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      <input
        className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        id={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        required
      />
    </div>
  </div>
);

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [userType, setUserType] = useState('alumni');
  const [formData, setFormData] = useState({
    fullName: '', email: '', password: '', confirmPassword: '', graduationYear: '',
    major: '', currentJob: '', company: '', phoneNumber: '', linkedIn: '',
    dateOfBirth: '', address: '', city: '', country: '', bio: '', skills: '',
    instituteName: '', industry: '', location: '', website: '', description: ''
  });

  const handleInputChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    const userData = { userType, ...formData };
    try {
      await signup(userData);
      toast.success('Signup successful!');
      
      // Navigate based on user type
      switch(userType) {
        case 'alumni':
          navigate('/dashboard');
          break;
        case 'institute':
          navigate('/instituteDashboard');
          break;
        case 'employer':
          navigate('/employerDashboard');
          break;
        case 'student':
          navigate('/studentDashboard');
          break;
        case 'faculty':
          navigate('/facultyDashboard');
          break;
        case 'admin':
          navigate('/admin');
        default:
          navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.message || 'Signup failed');
    }
  };


  const userTypes = [
    { id: 'alumni', label: 'Alumni', icon: FaUser },
    { id: 'student', label: 'Student', icon: FaGraduationCap },
    { id: 'faculty', label: 'Faculty', icon: FaChalkboardTeacher },
    { id: 'employer', label: 'Employer', icon: FaBriefcase },
    { id: 'institute', label: 'Institute', icon: FaBuilding },
    { id: 'admin', label: 'Admin', icon: FaUser }
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      <Toaster position="top-center" reverseOrder={false} />
      
      {/* Left side with image */}
      <div className="hidden lg:flex lg:w-2/5 bg-cover bg-center" style={{backgroundImage: "url('https://img.freepik.com/premium-vector/college-university-students-campus-community-vector-illustration_497046-2521.jpg?w=740')"}}>
        <div className="w-full flex flex-col justify-center items-center bg-black bg-opacity-50 text-white p-12">
          <h1 className="text-4xl font-bold mb-4">Join Our Network</h1>
          <p className="text-xl text-center">Connect with alumni, find opportunities, and grow your career.</p>
        </div>
      </div>
      
      {/* Right side with signup form */}
      <div className="w-full lg:w-3/5 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-12">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Create Your Account</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-wrap justify-center space-x-4 mb-6">
              {userTypes.map((type) => (
                <motion.button
                  key={type.id}
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setUserType(type.id)}
                  className={`flex flex-col items-center p-2 rounded-lg ${
                    userType === type.id ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  <type.icon className="text-xl mb-1" />
                  <span className="text-xs">{type.label}</span>
                </motion.button>
              ))}
            </div>

            <div className="flex flex-wrap -mx-2">
              {userType !== 'institute' ? (
                <>
                  <InputField icon={FaUser} type="text" placeholder="Full Name" value={formData.fullName} onChange={handleInputChange} name="fullName" />
                  <InputField icon={FaEnvelope} type="email" placeholder="Email Address" value={formData.email} onChange={handleInputChange} name="email" />
                  <InputField icon={FaLock} type="password" placeholder="Password" value={formData.password} onChange={handleInputChange} name="password" />
                  <InputField icon={FaLock} type="password" placeholder="Confirm Password" value={formData.confirmPassword} onChange={handleInputChange} name="confirmPassword" />
                  <InputField icon={FaCalendarAlt} type="date" placeholder="Date of Birth" value={formData.dateOfBirth} onChange={handleInputChange} name="dateOfBirth" />
                  <InputField icon={FaMapMarkerAlt} type="text" placeholder="Address" value={formData.address} onChange={handleInputChange} name="address" />
                  <InputField icon={FaMapMarkerAlt} type="text" placeholder="City" value={formData.city} onChange={handleInputChange} name="city" />
                  <InputField icon={FaGlobe} type="text" placeholder="Country" value={formData.country} onChange={handleInputChange} name="country" />
                  <InputField icon={FaPhone} type="tel" placeholder="Phone Number" value={formData.phoneNumber} onChange={handleInputChange} name="phoneNumber" />
                  <InputField icon={FaLinkedin} type="url" placeholder="LinkedIn Profile URL" value={formData.linkedIn} onChange={handleInputChange} name="linkedIn" />
                  
                  {(userType === 'alumni' || userType === 'student') && (
                    <>
                      <InputField icon={FaCalendarAlt} type="number" placeholder="Graduation Year" value={formData.graduationYear} onChange={handleInputChange} name="graduationYear" />
                      <InputField icon={FaGraduationCap} type="text" placeholder="Major/Field of Study" value={formData.major} onChange={handleInputChange} name="major" />
                    </>
                  )}

                  {(userType === 'alumni' || userType === 'employer') && (
                    <>
                      <InputField icon={FaBriefcase} type="text" placeholder="Current Job Title" value={formData.currentJob} onChange={handleInputChange} name="currentJob" />
                      <InputField icon={FaBuilding} type="text" placeholder="Company/Institution" value={formData.company} onChange={handleInputChange} name="company" />
                    </>
                  )}

                  <div className="mb-4 w-full px-2">
                    <textarea
                      className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Bio: Tell us about yourself"
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      rows="3"
                    />
                  </div>
                  <InputField icon={FaBookOpen} type="text" placeholder="Skills (comma-separated)" value={formData.skills} onChange={handleInputChange} name="skills" />
                </>
              ) : (
                <>
                  <InputField icon={FaBuilding} type="text" placeholder="Institute Name" value={formData.instituteName} onChange={handleInputChange} name="instituteName" />
                  <InputField icon={FaEnvelope} type="email" placeholder="Institute Email" value={formData.email} onChange={handleInputChange} name="email" />
                  <InputField icon={FaLock} type="password" placeholder="Password" value={formData.password} onChange={handleInputChange} name="password" />
                  <InputField icon={FaLock} type="password" placeholder="Confirm Password" value={formData.confirmPassword} onChange={handleInputChange} name="confirmPassword" />
                  <InputField icon={FaIndustry} type="text" placeholder="Industry" value={formData.industry} onChange={handleInputChange} name="industry" />
                  <InputField icon={FaMapMarkerAlt} type="text" placeholder="Location" value={formData.location} onChange={handleInputChange} name="location" />
                  <InputField icon={FaGlobe} type="url" placeholder="Website" value={formData.website} onChange={handleInputChange} name="website" />
                  <div className="mb-4 w-full px-2">
                    <textarea
                      className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Institute Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows="3"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="px-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold text-lg shadow-md hover:bg-blue-700 transition duration-300"
              >
                Sign Up
              </motion.button>
            </div>
          </form>
          
          <p className="mt-6 text-center text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-800 font-semibold">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;